import React, { memo, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { TURN_STATUS } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import ComposerControls from './Components/ComposerControls/ComposerControls'
import Conversation from './Components/Conversation/Conversation'
import NewImageNote from './Components/NewImageNote/NewImageNote'
import useCreateTab from './useCreateTab'

const selectImageSettings = state => state.imageSettings

// isFullWidth renders the tab as the Image Generator page rather than inside the assistant
// panel: the same conversation and composer, laid out across the whole content area.
function CreateTab({ isFullWidth = false }) {
  const t = useTranslation()
  const { twoDImagesIdeationFlowEnabled, unfinishedTwoDFeaturesEnabled } = useContext(ThreeDStudioPageContext)
  // The composer settings live in the store, shared by the panel and the Image Generator
  // page. Without the ideation flow the composer shows none of the controls, so its runs
  // keep the backend's provider ranking, default aspect ratio and single image.
  const imageSettings = useThreeDStudioStore(selectImageSettings)
  const hasGenerationSettings = Boolean(twoDImagesIdeationFlowEnabled)
  const {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isAccessPending,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  } = useCreateTab({ isFullWidth, hasGenerationSettings })
  const composerControls = (
    <ComposerControls
      imageSettings={imageSettings}
      hasStyleButton={Boolean(unfinishedTwoDFeaturesEnabled)}
      isFullWidth={isFullWidth}
    />
  )

  // A send while the access tier is still hydrating cannot be judged, so the guard in
  // handleSend would swallow it; the button waits instead.
  const isSendDisabled = isGenerating || isAccessPending || !prompt.trim()
  const hasGeneratedImage = messages.some(message => message.status === TURN_STATUS.DONE && message.results.length > 0)

  return (
    <div
      className={classnames(
        'image-assistant-create-tab__create',
        isFullWidth && 'image-assistant-create-tab__create--full-width',
      )}
    >
      <Conversation
        messages={messages}
        isGenerating={isGenerating}
        onApply={handleApply}
        onRegenerate={handleRegenerate}
        onGenerateAgain={handleGenerateAgain}
        onEdit={handleEdit}
        isFullWidth={isFullWidth}
      />
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={tags}
        fileInputRef={fileInputRef}
        customClass={isFullWidth ? 'image-assistant-create-tab__composer--full-width' : ''}
        sendLabel={t('imageAssistantPanel.createTab.composer.send')}
        notice={hasGeneratedImage ? <NewImageNote /> : null}
        controls={twoDImagesIdeationFlowEnabled ? composerControls : null}
        uploadLabel={twoDImagesIdeationFlowEnabled ? t('imageAssistantPanel.createTab.composer.addReferences') : null}
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        onPromptChange={handlePromptChange}
        onTagClick={handleTagClick}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

CreateTab.propTypes = {
  isFullWidth: bool,
}

export default memo(CreateTab)
